import React, { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminVerify, adminUpdateSettings, adminListCircles, fetchSettings, adminBroadcastLive, adminGenerateQuiz, fetchReaderToc } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, RotateCw } from "lucide-react";

const TOKEN_KEY = "lovegenome_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [settings, setSettings] = useState({
    launch_date: "", launch_enabled: false, featured_circle_id: "",
    live_active: false, live_title: "", live_message: "", live_url: "", live_duration_minutes: 60,
  });
  const [circles, setCircles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [liveNow, setLiveNow] = useState(null);
  // AI Quiz Draft
  const [toc, setToc] = useState([]);
  const [quizChapter, setQuizChapter] = useState("ch1");
  const [quizGenerating, setQuizGenerating] = useState(false);
  const [generatedQuiz, setGeneratedQuiz] = useState(null);

  const boot = async (tok) => {
    try {
      await adminVerify(tok);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, tok);
      const [s, cs] = await Promise.all([fetchSettings(), adminListCircles(tok)]);
      setSettings({
        launch_date: s.launch_date ? s.launch_date.slice(0, 16) : "",
        launch_enabled: !!s.launch_enabled,
        featured_circle_id: s.featured_circle?.id || "",
        live_active: !!s.live?.active,
        live_title: s.live?.title || "",
        live_message: s.live?.message || "",
        live_url: s.live?.url || "",
        live_duration_minutes: s.live?.duration_minutes || 60,
      });
      setLiveNow(s.live || null);
      setCircles(cs);
      // Load TOC for AI quiz drafting (best-effort)
      fetchReaderToc().then(setToc).catch(() => {});
    } catch {
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (token) boot(token);
    else setChecking(false);
  }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    await boot(token);
    if (!localStorage.getItem(TOKEN_KEY)) toast.error("Invalid admin token.");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        launch_enabled: settings.launch_enabled,
        featured_circle_id: settings.featured_circle_id || "",
        live_active: !!settings.live_active,
        live_title: settings.live_title || "",
        live_message: settings.live_message || "",
        live_url: settings.live_url || "",
        live_duration_minutes: Number(settings.live_duration_minutes) || 60,
      };
      if (settings.launch_date) payload.launch_date = new Date(settings.launch_date).toISOString();
      const res = await adminUpdateSettings(token, payload);
      setLiveNow(res.live || null);
      toast.success("Settings saved — the site updates instantly.");
    } catch {
      toast.error("Could not save. Check your token.");
    } finally {
      setSaving(false);
    }
  };

  const broadcast = async () => {
    if (!settings.live_active) {
      toast.error("Turn Live on and save before broadcasting.");
      return;
    }
    if (!window.confirm("Send an on-air email to every newsletter subscriber?")) return;
    setBroadcasting(true);
    try {
      const res = await adminBroadcastLive(token);
      toast.success(`Broadcast sent to ${res.sent} subscribers${res.failed ? ` (${res.failed} failed)` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed.");
    } finally {
      setBroadcasting(false);
    }
  };

  if (checking) {
    return <main className="min-h-screen bg-cream pt-40 flex justify-center"><span className="font-editorial italic text-2xl text-emerald">Loading…</span></main>;
  }

  if (!authed) {
    return (
      <main className="min-h-screen bg-emerald text-cream pt-32 flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm text-center">
          <Logo className="w-14 h-14 mx-auto mb-8" />
          <h1 className="font-display text-3xl mb-2">Admin</h1>
          <p className="font-body text-cream/70 text-sm mb-8">Enter your admin token to manage launch & circles.</p>
          <input
            type="password" value={token} onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token" data-testid="admin-token-input"
            className="w-full bg-transparent border-b border-cream/30 focus:border-gold outline-none py-3 text-center text-cream mb-6"
          />
          <button type="submit" className="btn-gold w-full justify-center" data-testid="admin-login-btn">Enter</button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24" data-testid="admin-panel">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="eyebrow text-emerald mb-4">Admin · Launch</p>
          <h1 className="font-display text-4xl md:text-5xl text-ink mb-12">Launch controls</h1>
        </Reveal>

        <div className="border border-line bg-surface p-8 md:p-10 space-y-8">
          <div>
            <label className="eyebrow text-ink/50 block mb-3">Release date & time</label>
            <input
              type="datetime-local" value={settings.launch_date}
              onChange={(e) => setSettings((s) => ({ ...s, launch_date: e.target.value }))}
              data-testid="admin-launch-date"
              className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer" data-testid="admin-launch-enabled">
            <input type="checkbox" checked={settings.launch_enabled}
              onChange={(e) => setSettings((s) => ({ ...s, launch_enabled: e.target.checked }))}
              className="w-5 h-5 accent-emerald" />
            <span className="font-body text-ink">Show countdown banner on the home page</span>
          </label>

          <div>
            <label className="eyebrow text-ink/50 block mb-3">Feature a circle on the home page</label>
            <Select value={settings.featured_circle_id || "none"} onValueChange={(v) => setSettings((s) => ({ ...s, featured_circle_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="admin-featured-circle" className="w-full bg-cream border-line rounded-none p-3 h-auto font-body"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="bg-cream">
                <SelectItem value="none">None</SelectItem>
                {circles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}, {c.country}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <button onClick={save} disabled={saving} className="btn-emerald disabled:opacity-60" data-testid="admin-save-btn">
            {saving ? "Saving…" : "Save & publish"}
          </button>
        </div>

        <div className="mt-16" data-testid="admin-live-section">
          <Reveal>
            <p className="eyebrow text-emerald mb-4">Admin · Live Author Moments</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-3">Go Live, unannounced.</h2>
            <p className="font-body text-ink/60 mb-10 max-w-2xl">
              Flip this on when you want the whole site to glow with a live drop-in.
              A gold banner slides down for every reader; you can optionally blast
              your subscribers with a one-tap email.
            </p>
          </Reveal>

          <div className="border border-line bg-surface p-8 md:p-10 space-y-6">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="admin-live-toggle">
              <input
                type="checkbox"
                checked={settings.live_active}
                onChange={(e) => setSettings((s) => ({ ...s, live_active: e.target.checked }))}
                className="w-5 h-5 accent-emerald"
              />
              <span className="font-body text-ink">
                {settings.live_active
                  ? "Live is ON — banner is showing site-wide"
                  : "Turn on the Live banner"}
              </span>
              {liveNow?.active && (
                <span className="inline-flex items-center gap-1.5 ml-3 bg-gold text-ink text-[10px] font-medium uppercase tracking-widest px-2 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" /> On-Air
                </span>
              )}
            </label>

            <div>
              <label className="eyebrow text-ink/50 block mb-3">Headline (what readers see)</label>
              <input
                type="text"
                value={settings.live_title}
                onChange={(e) => setSettings((s) => ({ ...s, live_title: e.target.value }))}
                placeholder="e.g. Felicia is live on Instagram right now"
                maxLength={140}
                data-testid="admin-live-title"
                className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body"
              />
            </div>

            <div>
              <label className="eyebrow text-ink/50 block mb-3">Short message (optional)</label>
              <textarea
                value={settings.live_message}
                onChange={(e) => setSettings((s) => ({ ...s, live_message: e.target.value }))}
                placeholder="A quiet, honest note — what this drop-in is about."
                rows={2}
                maxLength={600}
                data-testid="admin-live-message"
                className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Join URL (Zoom / IG Live / YouTube)</label>
                <input
                  type="url"
                  value={settings.live_url}
                  onChange={(e) => setSettings((s) => ({ ...s, live_url: e.target.value }))}
                  placeholder="https://…"
                  data-testid="admin-live-url"
                  className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body"
                />
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Auto-off after (minutes)</label>
                <input
                  type="number"
                  min={5}
                  max={480}
                  value={settings.live_duration_minutes}
                  onChange={(e) => setSettings((s) => ({ ...s, live_duration_minutes: e.target.value }))}
                  data-testid="admin-live-duration"
                  className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={save}
                disabled={saving}
                className="btn-emerald disabled:opacity-60"
                data-testid="admin-live-save"
              >
                {saving ? "Saving…" : (settings.live_active ? "Save & keep live on" : "Save")}
              </button>
              <button
                onClick={broadcast}
                disabled={broadcasting || !settings.live_active || !liveNow?.active}
                className="btn-gold disabled:opacity-60"
                data-testid="admin-live-broadcast"
                title={!settings.live_active ? "Save with Live turned ON first" : ""}
              >
                {broadcasting ? "Sending…" : "Blast subscribers"}
              </button>
            </div>
            <p className="text-xs text-ink/45 font-body">
              The banner auto-disappears after your duration expires. Readers can dismiss it too —
              once dismissed, they won't see it again until you save a new session.
            </p>
          </div>
        </div>

        <div className="mt-16" data-testid="admin-quiz-section">
          <Reveal>
            <p className="eyebrow text-emerald mb-4">Admin · AI Quiz Draft (Claude Haiku)</p>
            <h2 className="font-display text-3xl md:text-4xl text-ink mb-3">Draft a chapter quiz in seconds.</h2>
            <p className="font-body text-ink/60 mb-10 max-w-2xl">
              Pick a chapter, click Generate. Claude drafts five insight questions (2 MCQ + 1 True/False + 2 scenario) grounded in that chapter. Review, edit, then paste the JSON into <code className="bg-surface px-1.5 py-0.5 text-xs">/app/backend/assets/quizzes.json</code> to publish.
            </p>
          </Reveal>

          <div className="border border-line bg-surface p-8 md:p-10 space-y-6">
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="eyebrow text-ink/50 block mb-3">Chapter</label>
                <select
                  value={quizChapter}
                  onChange={(e) => setQuizChapter(e.target.value)}
                  data-testid="admin-quiz-chapter"
                  className="w-full bg-cream border border-line focus:border-emerald outline-none p-3 font-body"
                >
                  {toc.length === 0 && <option value="ch1">Chapter 1</option>}
                  {toc.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}{c.title ? ` — ${c.title}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={async () => {
                  setQuizGenerating(true);
                  setGeneratedQuiz(null);
                  try {
                    const res = await adminGenerateQuiz(token, quizChapter);
                    setGeneratedQuiz(res.quiz);
                    toast.success("Draft ready — review below.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Generation failed. Try again.");
                  } finally {
                    setQuizGenerating(false);
                  }
                }}
                disabled={quizGenerating}
                className="btn-emerald w-full justify-center disabled:opacity-60"
                data-testid="admin-quiz-generate"
              >
                {quizGenerating ? <><RotateCw className="w-4 h-4 animate-spin" /> Drafting…</> :
                  <><Sparkles className="w-4 h-4" /> Generate draft</>}
              </button>
            </div>

            {generatedQuiz && (
              <div className="pt-6 border-t border-line" data-testid="admin-quiz-preview">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="eyebrow text-emerald mb-1">{generatedQuiz.badge || "Draft"}</p>
                    <p className="font-display text-2xl text-ink">{generatedQuiz.title || "Chapter Insight Check"}</p>
                  </div>
                  <button
                    onClick={() => {
                      const payload = { [quizChapter]: { chapter_id: quizChapter, ...generatedQuiz } };
                      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                      toast.success("JSON copied to clipboard.");
                    }}
                    className="btn-outline text-xs"
                    data-testid="admin-quiz-copy"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy JSON
                  </button>
                </div>
                <ol className="space-y-4">
                  {(generatedQuiz.questions || []).map((q, qi) => (
                    <li key={qi} className="p-4 bg-cream border border-line" data-testid={`admin-quiz-q-${qi}`}>
                      <p className="text-[10px] uppercase tracking-widest text-ink/45 font-body mb-1">
                        {q.type === "mcq" ? "Multiple Choice" : q.type === "tf" ? "True or False" : "Scenario"}
                      </p>
                      <p className="font-display text-lg text-ink mb-3">{q.prompt}</p>
                      <ul className="space-y-1 mb-2">
                        {(q.choices || []).map((ch, ci) => (
                          <li key={ci} className={`text-sm font-body ${ci === q.correct_index ? "text-emerald font-semibold" : "text-ink/75"}`}>
                            {ci === q.correct_index ? "✓ " : "○ "}{ch}
                          </li>
                        ))}
                      </ul>
                      {q.explain && (
                        <p className="text-xs font-body text-ink/60 italic border-l-2 border-gold pl-3">{q.explain}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Admin;
